import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';

const DEFAULT_COLOR = '#3b82f6';
type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

export default function TissButton({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
    const { t } = useTranslation();
    let cookie = getCookies();

    // Helper to get multilingual value
    const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
        if (!field) return fallback;
        if (typeof field === 'string') return field;
        if (typeof field === 'object') {
            return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
        }
        return fallback;
    };

    // Helper to set multilingual value in list items
    const normalizeListForLanguage = (list: any[], lang: LanguageCode): any[] => {
        return list.map(item => ({
            ...item,
            text: getMultilingualValue(item.text, lang, item.text || '')
        }));
    };

    // Initialize multilingual list from props
    const initializeMultilingualList = () => {
        const rawList = data.data?.list || [];
        return rawList.map((item: any) => ({
            ...item,
            // Ensure text is preserved as multilingual object
            text: typeof item.text === 'string' 
                ? { en: item.text } 
                : (item.text || {})
        }));
    };

    // State - Store ALL multilingual list data (all 5 languages at once)
    const [multilingualList, setMultilingualList] = React.useState<any[]>(initializeMultilingualList());
    
    // Get current language list from multilingual state
    const getCurrentListForLang = () => {
        return multilingualList.map(item => ({
            ...item,
            text: getMultilingualValue(item.text, activeLang, '')
        }));
    };
    
    const currentList = getCurrentListForLang();
    const hasContent = currentList.length > 0;

    const [isEditing, setIsEditing] = React.useState(!hasContent);
    const prevLangRef = React.useRef(activeLang);
    const prevDataIdRef = React.useRef(data?._id);

    const [text, setText] = React.useState<string>('');
    const [link, setLink] = React.useState<string>('');
    const [color, setColor] = React.useState<string>(DEFAULT_COLOR);
    const [saving, setSaving] = React.useState(false);
    const [hasChanges, setHasChanges] = React.useState(false);
    const [editingItem, setEditingItem] = React.useState<number | null>(null);
    const [editingField, setEditingField] = React.useState<'text' | 'link' | null>(null);

    // Update multilingual list when data from server changes
    React.useEffect(() => {
        if (prevDataIdRef.current !== data?._id) {
            const rawList = data.data?.list || [];
            
            // Initialize multilingual list from server
            const newMultilingualList = rawList.map((item: any) => ({
                ...item,
                // Ensure text is preserved as multilingual object
                text: typeof item.text === 'string' 
                    ? { en: item.text } 
                    : (item.text || {})
            }));
            
            setMultilingualList(newMultilingualList);
            setHasChanges(false);
            prevDataIdRef.current = data?._id;
        }
        
        // Update ref when language changes
        if (prevLangRef.current !== activeLang) {
            setHasChanges(false);
            prevLangRef.current = activeLang;
        }
    }, [data?._id, data?.data?.list, activeLang]);

    // Track changes - compare current list with multilingual state
    React.useEffect(() => {
        const originalList = data.data?.list || [];
        const originalForLang = normalizeListForLanguage(originalList, activeLang);
        const dataEqual = JSON.stringify(currentList) === JSON.stringify(originalForLang);
        setHasChanges(!dataEqual);
    }, [currentList, activeLang, data?.data?.list]);

    const addButton = () => {
        if (!text.trim()) {
            Swal.fire({
                title: t('Error'),
                text: t('Please enter a button label'),
                icon: 'warning',
            });
            return;
        }

        // Add new item to multilingual list
        const newItem = {
            text: { [activeLang]: text.trim() },
            link: link.trim() || '#',
            color: color || DEFAULT_COLOR,
            arrange: multilingualList.length + 1,
        };
        
        setMultilingualList((prev: any[]) => [...prev, newItem]);
        setText('');
        setLink('');
        setColor(DEFAULT_COLOR);
    };

    const removeItem = async (index: number) => {
        const result = await Swal.fire({
            title: t('Delete button?'),
            text: t('This action cannot be undone'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete'),
            cancelButtonText: t('Cancel'),
        });

        if (result.isConfirmed) {
            setMultilingualList((prev: any[]) => 
                prev.filter((_, i) => i !== index).map((item, idx) => ({
                    ...item,
                    arrange: idx + 1,
                }))
            );
        }
    };

    const moveItem = (index: number, direction: 'up' | 'down') => {
        setMultilingualList((prev: any[]) => {
            const newList = [...prev];
            const targetIndex = direction === 'up' ? index - 1 : index + 1;

            if (targetIndex >= 0 && targetIndex < newList.length) {
                [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
                newList[index].arrange = index + 1;
                newList[targetIndex].arrange = targetIndex + 1;
            }
            return newList;
        });
    };

    const editItem = (index: number, field: 'text' | 'link' | 'color', value: string) => {
        setMultilingualList((prev: any[]) => 
            prev.map((item, idx) => {
                if (idx === index) {
                    if (field === 'text') {
                        // Update only the current language, preserve all others
                        const currentText = item.text || {};
                        const updatedText = typeof currentText === 'string' 
                            ? { [activeLang]: value }
                            : { ...currentText, [activeLang]: value };
                        return { ...item, text: updatedText };
                    } else {
                        // For link and color, update directly
                        return { ...item, [field]: value };
                    }
                }
                return item;
            })
        );
    };

    const save = async () => {
        if (!data?._id) return;

        setSaving(true);
        try {
            console.log('=== SAVING ALL MULTILINGUAL BUTTON LIST ===');
            console.log('All languages in state:', multilingualList);
            
            // Send ALL multilingual list to backend
            const dataToSend = {
                list: multilingualList // Send complete multilingual list
            };
            
            console.log('Sending to backend:', JSON.stringify(dataToSend, null, 2));

            await axios.put(
                `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
                dataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${cookie['token']}`,
                    },
                }
            );

            Swal.fire({
                title: t('Saved successfully'),
                icon: 'success',
                timer: 1200,
                showConfirmButton: false,
            });
            setIsEditing(false);
            setHasChanges(false);
            if (reload) reload();
        } catch (error: any) {
            console.error('Error saving data:', error);
            Swal.fire({
                title: t('Error saving'),
                icon: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (hasChanges) {
            Swal.fire({
                title: t('Discard changes?'),
                text: t('You have unsaved changes. Are you sure you want to cancel?'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('Yes, discard'),
                cancelButtonText: t('No, keep editing'),
            }).then((result) => {
                if (result.isConfirmed) {
                    // Reload from server to reset all changes
                    reload();
                    setIsEditing(false);
                    setHasChanges(false);
                }
            });
        } else {
            setIsEditing(false);
        }
    };

    // Empty state
    if (!hasContent && !isEditing) {
        return (
            <div
                className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
                onClick={() => setIsEditing(true)}
                dir="rtl"
            >
                <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                </div>
                <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
                    {t('Click to add button')}
                </span>
                <span className="text-gray-400 text-xs mt-1">
                    {t('Create clickable buttons with links')}
                </span>
            </div>
        );
    }

    // Preview Mode
    if (!isEditing) {
        return (
            <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
                <div className="p-0.5 relative">
                    {currentList.length > 0 ? (
                        <div className="flex flex-wrap gap-4 p-6 justify-center">
                            {currentList.map((item, index) => (
                                <a
                                    key={index}
                                    href={item.link || '#'}
                                    target={item.link?.startsWith('http') ? '_blank' : '_self'}
                                    rel={item.link?.startsWith('http') ? 'noopener noreferrer' : undefined}
                                    className="inline-block"
                                >
                                    <button
                                        className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 min-w-[120px]"
                                        style={{
                                            backgroundColor: item.color || DEFAULT_COLOR,
                                            boxShadow: `0 10px 25px -5px ${item.color || DEFAULT_COLOR}50`,
                                        }}
                                    >
                                        {item.text || t('Button')}
                                    </button>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 text-gray-400">
                            <div className="flex flex-col items-center">
                                <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                </svg>
                                <p className="text-sm">{t('No buttons yet')}</p>
                            </div>
                        </div>
                    )}

                    {/* Edit Button - Appears on hover */}
                    {currentList.length > 0 && (
                        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('Edit Buttons')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
            <div className="p-0.5">
                <div className="space-y-4">
                    {/* Add New Button */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-100">
                        <h3 className="text-base font-bold text-gray-800 mb-4 text-right">{t('Add New Button')}</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                    {t('Button Label')}
                                </label>
                                <Input
                                    value={text}
                                    onChange={(value) => setText(value || '')}
                                    placeholder={t('Enter button text...')}
                                    onPressEnter={addButton}
                                    style={{
                                        padding: '10px 16px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        textAlign: 'right',
                                    }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                        {t('Link URL')}
                                    </label>
                                    <Input
                                        value={link}
                                        onChange={(value) => setLink(value || '')}
                                        placeholder={t('Enter link...')}
                                        onPressEnter={addButton}
                                        style={{
                                            padding: '10px 16px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '10px',
                                            direction: 'ltr',
                                            textAlign: 'left',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                        {t('Button Color')}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="color"
                                            value={color}
                                            onChange={(e) => setColor(e.target.value)}
                                            className="w-full h-12 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-500 hover:scale-105 transition-all shadow-md hover:shadow-lg"
                                        />
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={addButton}
                                className="w-full px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                                {t('Add Button')}
                            </button>
                        </div>
                    </div>

                    {/* Live Preview */}
                    {currentList.length > 0 && (
                        <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 shadow-xl">
                            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-blue-100 rounded-lg p-2">
                                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 text-right">{t('Live Preview')}</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">{t('Changes update instantly')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                    <span className="text-xs text-green-700 font-semibold">{t('Live')}</span>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-4 justify-center bg-white rounded-lg p-4 border border-gray-100">
                                {currentList.map((item, index) => (
                                    <button
                                        key={index}
                                        className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 min-w-[120px]"
                                        style={{
                                            backgroundColor: item.color || DEFAULT_COLOR,
                                            boxShadow: `0 10px 25px -5px ${item.color || DEFAULT_COLOR}50`,
                                        }}
                                    >
                                        {item.text || t('Button')}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Buttons List Editor */}
                    {currentList.length > 0 && (
                        <div className="bg-white rounded-xl p-5 border-2 border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-purple-100 rounded-lg p-2">
                                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-gray-800 text-right">{t('Buttons List')}</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">{t('Manage your buttons')}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {currentList.map((item, index) => (
                                    <div
                                        key={index}
                                        className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border-2 border-gray-200 hover:border-blue-300 transition-all duration-300 group/item"
                                    >
                                        <div className="flex items-center gap-4">
                                            {/* Number Badge */}
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg font-extrabold text-white text-lg bg-gradient-to-br from-gray-600 to-gray-700 border-2 border-white">
                                                    {index + 1}
                                                </div>
                                            </div>

                                            {/* Preview Button */}
                                            <div className="flex-shrink-0">
                                                <button
                                                    className="px-5 py-2.5 rounded-lg font-semibold text-white shadow-md transition-all duration-300 group-hover/item:scale-105"
                                                    style={{
                                                        backgroundColor: item.color || DEFAULT_COLOR,
                                                        boxShadow: `0 8px 20px -8px ${item.color || DEFAULT_COLOR}60`,
                                                    }}
                                                >
                                                    {item.text || t('Button')}
                                                </button>
                                            </div>

                                            {/* Form Fields */}
                                            <div className="flex-1 space-y-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                                        {t('Label')}
                                                    </label>
                                                    {editingItem === index && editingField === 'text' ? (
                                                        <Input
                                                            key={`button-text-${index}-${activeLang}`}
                                                            value={item.text || ''}
                                                            onChange={(value) => editItem(index, 'text', value || '')}
                                                            onBlur={() => {
                                                                setEditingItem(null);
                                                                setEditingField(null);
                                                            }}
                                                            onPressEnter={() => {
                                                                setEditingItem(null);
                                                                setEditingField(null);
                                                            }}
                                                            autoFocus
                                                            style={{
                                                                padding: '8px 12px',
                                                                border: '2px solid #3b82f6',
                                                                borderRadius: '8px',
                                                                textAlign: 'right',
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            onClick={() => {
                                                                setEditingItem(index);
                                                                setEditingField('text');
                                                            }}
                                                            className="px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 cursor-pointer transition-colors text-gray-700 font-medium"
                                                        >
                                                            {item.text || <span className="text-gray-400 italic">{t('Click to edit')}</span>}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                                            {t('Link')}
                                                        </label>
                                                        {editingItem === index && editingField === 'link' ? (
                                                            <Input
                                                                value={item.link || ''}
                                                                onChange={(value) => editItem(index, 'link', value || '')}
                                                                onBlur={() => {
                                                                    setEditingItem(null);
                                                                    setEditingField(null);
                                                                }}
                                                                onPressEnter={() => {
                                                                    setEditingItem(null);
                                                                    setEditingField(null);
                                                                }}
                                                                autoFocus
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    border: '2px solid #3b82f6',
                                                                    borderRadius: '8px',
                                                                    direction: 'ltr',
                                                                    textAlign: 'left',
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                onClick={() => {
                                                                    setEditingItem(index);
                                                                    setEditingField('link');
                                                                }}
                                                                className="px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 cursor-pointer transition-colors text-gray-700 font-medium text-left"
                                                            >
                                                                {item.link || <span className="text-gray-400 italic">{t('Click to edit')}</span>}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 text-right">
                                                            {t('Color')}
                                                        </label>
                                                        <div className="relative group/color">
                                                            <input
                                                                type="color"
                                                                value={item.color || DEFAULT_COLOR}
                                                                onChange={(e) => editItem(index, 'color', e.target.value)}
                                                                className="w-full h-11 rounded-lg border-2 border-gray-300 cursor-pointer hover:border-blue-500 hover:scale-105 transition-all shadow-md hover:shadow-lg"
                                                                title={t('Change color')}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex flex-col gap-2 flex-shrink-0">
                                                <button
                                                    onClick={() => moveItem(index, 'up')}
                                                    disabled={index === 0}
                                                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                                                    title={t('Move up')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => moveItem(index, 'down')}
                                                    disabled={index === currentList.length - 1}
                                                    className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                                                    title={t('Move down')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => removeItem(index)}
                                                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-md hover:shadow-lg"
                                                    title={t('Delete')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty State in Edit Mode */}
                    {currentList.length === 0 && (
                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-12 border-2 border-dashed border-blue-200 text-center">
                            <div className="bg-blue-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                </svg>
                            </div>
                            <p className="text-gray-700 font-semibold mb-2 text-base">{t('No buttons yet')}</p>
                            <p className="text-sm text-gray-500 max-w-md mx-auto">{t('Add buttons above to get started')}</p>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3 justify-end items-center">
                    {hasChanges && (
                        <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                            {t('Unsaved changes')}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={saving}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {t('Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !hasChanges}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>{t('Saving...')}</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{t('Save')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}